import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  Renderer2,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IsActiveMatchOptions, NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '@services/auth.service';
import { ErrorReportingService } from '@services/error-reporting.service';
import { filter } from 'rxjs';

interface DashboardMenuUser {
  initials: string;
  name: string;
}

interface DashboardRoute {
  icon: string;
  label: string;
  path: string;
}

@Component({
  imports: [NgTemplateOutlet, RouterLink, RouterLinkActive],
  selector: 'app-dashboard-menu',
  styleUrl: './dashboard-menu.component.scss',
  templateUrl: './dashboard-menu.component.html',
})
export class DashboardMenuComponent {
  readonly overlayActive = input(false);
  protected readonly activeMatchOptions: IsActiveMatchOptions = {
    fragment: 'ignored',
    matrixParams: 'ignored',
    paths: 'subset',
    queryParams: 'ignored',
  };
  protected readonly dashboardRoutes: DashboardRoute[] = [
    { icon: 'developers', label: 'Desarrolladores', path: '/dashboard/developers' },
    { icon: 'projects', label: 'Proyectos', path: '/dashboard/projects' },
    { icon: 'benefits', label: 'Beneficios', path: '/dashboard/benefits' },
  ];
  protected isMobile = signal(false);
  protected isOpen = signal(false);
  protected user = computed(() => this.updateUser());
  private authService = inject(AuthService);
  private closeButton = viewChild<ElementRef<HTMLButtonElement>>('closeButton');
  private desktopMenu = viewChild<ElementRef<HTMLElement>>('desktopMenu');
  private destroyRef = inject(DestroyRef);
  private dialog = viewChild<ElementRef<HTMLDialogElement>>('dialog');
  private document = inject(DOCUMENT);
  private errorReportingService = inject(ErrorReportingService);
  private hamburger = viewChild<ElementRef<HTMLButtonElement>>('hamburger');
  private injector = inject(Injector);
  private mediaQuery = this.document.defaultView?.matchMedia('(width < 992px)');
  private renderer = inject(Renderer2);
  private router = inject(Router);

  constructor() {
    this.initializeViewport();
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe(() => this.close());
    effect(() => {
      if (this.overlayActive()) {
        this.close(false);
      }
    });
    this.destroyRef.onDestroy(() => this.close(false));
  }

  protected close(restoreFocus = true): void {
    if (!this.isOpen()) {
      return;
    }
    this.dialog()?.nativeElement.close();
    this.isOpen.set(false);
    this.renderer.removeClass(this.document.body, 'dashboard-menu-open');
    if (restoreFocus && this.isMobile() && !this.overlayActive()) {
      this.hamburger()?.nativeElement.focus();
    }
  }

  protected onCancel(event: Event): void {
    event.preventDefault();
    this.close();
  }

  protected onDialogClosed(): void {
    // Native close events are queued; an older event must not close a reopened menu.
    if (!this.dialog()?.nativeElement.open) {
      this.close();
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') {
      return;
    }
    const controls = this.dialog()?.nativeElement.querySelectorAll<HTMLElement>('button:not([disabled]):not([tabindex="-1"]), a[href]');
    const first = controls?.[0];
    const last = controls?.[controls.length - 1];
    const destination = event.shiftKey ? last : first;
    const boundary = event.shiftKey ? first : last;
    if (destination && this.document.activeElement === boundary) {
      event.preventDefault();
      destination.focus();
    }
  }

  protected open(): void {
    const dialog = this.dialog()?.nativeElement;
    if (!this.isMobile() || this.isOpen() || this.overlayActive() || !dialog) {
      return;
    }
    dialog.showModal();
    this.isOpen.set(true);
    this.renderer.addClass(this.document.body, 'dashboard-menu-open');
    this.closeButton()?.nativeElement.focus();
  }

  protected async signOut(): Promise<void> {
    this.close();
    try {
      await this.authService.signOut();
      await this.router.navigate(['/']);
    } catch (error) {
      this.errorReportingService.log('DashboardMenuComponent.signOut()', 'sign-out', error);
    }
  }

  private focusDesktopRoute(): void {
    const activeRoute = this.dashboardRoutes.find((route) => this.router.isActive(route.path, this.activeMatchOptions));
    if (activeRoute) {
      this.desktopMenu()?.nativeElement.querySelector<HTMLElement>(`a[href="${activeRoute.path}"]`)?.focus();
    }
  }

  private initializeViewport(): void {
    this.isMobile.set(this.mediaQuery?.matches ?? false);
    const listener = (event: MediaQueryListEvent): void => this.onViewportChange(event.matches);
    this.mediaQuery?.addEventListener('change', listener);
    this.destroyRef.onDestroy(() => this.mediaQuery?.removeEventListener('change', listener));
  }

  private onViewportChange(isMobile: boolean): void {
    const menuHadFocus = this.isOpen() || !!this.desktopMenu()?.nativeElement.contains(this.document.activeElement);
    this.close(false);
    this.isMobile.set(isMobile);
    if (menuHadFocus) {
      afterNextRender(() => {
        if (isMobile) {
          this.hamburger()?.nativeElement.focus();
        } else {
          this.focusDesktopRoute();
        }
      }, { injector: this.injector });
    }
  }

  private updateUser(): DashboardMenuUser | null {
    const authentication = this.authService.authentication();
    if (!authentication.userSession) {
      return null;
    }
    return {
      initials: authentication.userSession.name.split(' ').map((name) => name[0]).join('').toUpperCase(),
      name: authentication.userSession.name,
    };
  }
}
